import { FlyoutOpenState, FlyoutSide, LessonMode } from '../../Constants';
import lessonManager from '../managers/LessonManager';
import navMenuManager from '../managers/NavMenuManager';
import toolbarManager from '../managers/ToolbarManager';

export const LeftRailTab = {
  GOTO: 'goto',
  SATELLITE: 'satellite',
  DIRECTIONS: 'directions',
  INTERACTIVE: 'interactive',
  FLYOUT: 'flyout'
};

/**
 * Coordinates the left-edge tab strip (GO TO, Related Resources, and other
 * left flyouts) as one shared open/closed rail with a single selected panel.
 *
 * Opening the rail from a tab selects that tab. Only one panel occupies the
 * open column; switching tabs replaces that panel instead of stacking another
 * to the right. Tabs that are missing on a given page do not render; when they
 * return they join the list and do not steal selection.
 */
export class LeftRailService {
  postSatelliteMessage = (type) => {
    window.parent.postMessage({ type }, toolbarManager.externalUrl);
  }

  get isOpen() {
    return !!(lessonManager.isSatelliteOpen || (this.hasGoto() && navMenuManager.gotoMenuOpen));
  }

  get selectedTab() {
    return navMenuManager.leftRailSelectedTab;
  }

  get isSatellitePanelShowing() {
    return this.isOpen && this.selectedTab === LeftRailTab.SATELLITE && this.hasRelatedTab();
  }

  hasGoto = () => {
    return !!lessonManager.lessonOptions?.hasVerticalGotoMenu;
  }

  hasRelatedTab = () => {
    return !!lessonManager.shouldShowRelatedTabButton &&
      lessonManager.playerMode !== LessonMode.TEACHER_PRESENTATION;
  }

  hasDirections = () => {
    return !!navMenuManager.leftRailDirections;
  }

  hasLeftRail = () => {
    return this.hasGoto() || this.hasRelatedTab() || this.hasDirections();
  }

  registerDirections = (info) => {
    const current = navMenuManager.leftRailDirections;
    if (!info && !current) {
      return;
    }
    if (info && current &&
      current.directions === info.directions &&
      current.lessonElementId === info.lessonElementId) {
      return;
    }
    navMenuManager.setLeftRailDirections(info);
    if (!info && this.selectedTab === LeftRailTab.DIRECTIONS && this.hasGoto()) {
      this.setSelectedTab(LeftRailTab.GOTO);
    }
  }

  defaultSelectedTab = () => {
    if (this.hasGoto()) {
      return LeftRailTab.GOTO;
    }
    if (this.hasRelatedTab()) {
      return LeftRailTab.SATELLITE;
    }
    return LeftRailTab.FLYOUT;
  }

  /**
   * Whether the currently selected left-rail tab still has content on this page.
   * Flyout availability is passed in because hosting is decided by layout, not the
   * service.
   */
  isSelectedTabAvailable = ({ hasFlyout = false } = {}) => {
    const tab = this.selectedTab;
    if (tab === LeftRailTab.GOTO) {
      return this.hasGoto();
    }
    if (tab === LeftRailTab.SATELLITE) {
      return this.hasRelatedTab();
    }
    if (tab === LeftRailTab.DIRECTIONS) {
      return this.hasDirections();
    }
    if (tab === LeftRailTab.FLYOUT) {
      return !!hasFlyout;
    }
    return false;
  }

  /**
   * After navigation, tabs that existed on the previous slide may be gone while
   * the rail stays open. If the selected tab no longer has a panel, fall back so
   * GO TO (or another available tab) content is shown instead of an empty column.
   */
  reconcileSelectedTab = ({ hasFlyout = false } = {}) => {
    if (!this.isOpen) {
      return;
    }
    if (this.isSelectedTabAvailable({ hasFlyout })) {
      return;
    }
    const previousTab = this.selectedTab;
    let fallback = null;
    if (this.hasGoto()) {
      fallback = LeftRailTab.GOTO;
    } else if (this.hasRelatedTab()) {
      fallback = LeftRailTab.SATELLITE;
    } else if (this.hasDirections()) {
      fallback = LeftRailTab.DIRECTIONS;
    } else if (hasFlyout) {
      fallback = LeftRailTab.FLYOUT;
    }
    if (!fallback || fallback === previousTab) {
      return;
    }
    // Related overlay is owned by the parent; leaveSatellite selection does not
    // always clear it via syncSatelliteOverlay when the tab is already hidden.
    if (previousTab === LeftRailTab.SATELLITE) {
      this.postSatelliteMessage('close_satellite');
    }
    this.setSelectedTab(fallback, { echoSatellite: false });
  }

  closeDirections = () => {
    if (lessonManager.isDirectionsFlyoutOpen) {
      lessonManager.setIsDirectionsFlyoutOpen(false);
    }
  }

  closeInteractive = () => {
    if (lessonManager.isInteractiveFlyoutOpen) {
      lessonManager.setIsInteractiveFlyoutOpen(false);
    }
  }

  closeLeftFlyouts = () => {
    navMenuManager.flyoutOpenStates.forEach((state, lessonElementId) => {
      if (state && state.side === FlyoutSide.LEFT && state.open !== FlyoutOpenState.CLOSED) {
        navMenuManager.setFlyoutOpenState({
          open: FlyoutOpenState.CLOSED,
          tab: state.tab,
          side: state.side
        }, lessonElementId);
      }
    });
  }

  syncSatelliteOverlay = (echoSatellite, overlayWasShowing) => {
    if (!echoSatellite) {
      return;
    }
    const overlayIsShowing = this.isSatellitePanelShowing;
    if (overlayIsShowing && !overlayWasShowing) {
      this.postSatelliteMessage('open_satellite');
      return;
    }
    if (!overlayIsShowing && overlayWasShowing) {
      this.postSatelliteMessage('close_satellite');
    }
  }

  closeUnselectedInLessonPanels = (tab) => {
    if (tab !== LeftRailTab.DIRECTIONS) {
      this.closeDirections();
    }
    if (tab !== LeftRailTab.FLYOUT) {
      this.closeLeftFlyouts();
    }
  }

  setSelectedTab = (tab, { echoSatellite = true } = {}) => {
    const overlayWasShowing = this.isSatellitePanelShowing;
    if (navMenuManager.leftRailSelectedTab !== tab) {
      navMenuManager.setLeftRailSelectedTab(tab);
    }
    this.closeUnselectedInLessonPanels(tab);
    this.syncSatelliteOverlay(echoSatellite, overlayWasShowing);
  }

  /**
   * Keep the rail open/closed. `selectedTab` is which panel is showing.
   * Unhiding a tab must not call this with a new selection.
   */
  setOpen = (isOpen, { echoSatellite = true, selectedTab } = {}) => {
    const overlayWasShowing = this.isSatellitePanelShowing;

    if (isOpen) {
      if (this.hasGoto() && !navMenuManager.gotoMenuOpen) {
        navMenuManager.toggleGotoMenuState(true);
      }
      const nextSelected = selectedTab || this.selectedTab || this.defaultSelectedTab();
      if (navMenuManager.leftRailSelectedTab !== nextSelected) {
        navMenuManager.setLeftRailSelectedTab(nextSelected);
      }
      this.closeUnselectedInLessonPanels(nextSelected);
    } else if (this.hasGoto() && navMenuManager.gotoMenuOpen) {
      navMenuManager.toggleGotoMenuState(false);
    }

    if (lessonManager.isSatelliteOpen !== isOpen) {
      lessonManager.setIsSatelliteOpen(isOpen);
    }

    if (!isOpen) {
      this.closeDirections();
      this.closeInteractive();
      this.closeLeftFlyouts();
      navMenuManager.setLeftRailSelectedTab(null);
      if (echoSatellite && overlayWasShowing) {
        this.postSatelliteMessage('close_satellite');
      }
      return;
    }

    this.syncSatelliteOverlay(echoSatellite, overlayWasShowing);
  }

  open = (options) => {
    this.setOpen(true, options);
  }

  close = (options) => {
    this.setOpen(false, options);
  }

  closeAll = () => {
    this.close();
  }

  /**
   * An in-lesson left flyout (alignments, notes, etc.) is taking the rail slot.
   * If the rail is already open, this switches selection so the flyout occupies
   * the open column instead of stacking to the right of GO TO / related content.
   */
  prepareInLessonLeftPanelOpen = () => {
    if (!this.hasLeftRail()) {
      return;
    }
    this.open({ selectedTab: LeftRailTab.FLYOUT });
  }

  toggleDirections = () => {
    if (!this.hasLeftRail()) {
      lessonManager.setIsDirectionsFlyoutOpen(!lessonManager.isDirectionsFlyoutOpen);
      return;
    }
    if (!this.isOpen) {
      this.open({ selectedTab: LeftRailTab.DIRECTIONS });
      lessonManager.setIsDirectionsFlyoutOpen(true);
      return;
    }
    if (this.selectedTab === LeftRailTab.DIRECTIONS) {
      this.close();
      return;
    }
    this.setSelectedTab(LeftRailTab.DIRECTIONS);
    lessonManager.setIsDirectionsFlyoutOpen(true);
  }

  toggleSatellite = () => {
    if (!this.isOpen) {
      this.open({ selectedTab: LeftRailTab.SATELLITE });
      return;
    }
    if (this.selectedTab === LeftRailTab.SATELLITE) {
      this.close();
      return;
    }
    this.setSelectedTab(LeftRailTab.SATELLITE);
  }

  toggleGoto = (toggle) => {
    if (!this.hasGoto()) {
      if (typeof toggle !== 'undefined') {
        navMenuManager.toggleGotoMenuState(!!toggle);
      } else {
        navMenuManager.toggleGotoMenuState();
      }
      return;
    }
    if (typeof toggle !== 'undefined') {
      if (toggle) {
        this.open({ selectedTab: LeftRailTab.GOTO });
      } else {
        this.close();
      }
      return;
    }
    if (!this.isOpen) {
      this.open({ selectedTab: LeftRailTab.GOTO });
      return;
    }
    if (this.selectedTab === LeftRailTab.GOTO) {
      this.close();
      return;
    }
    this.setSelectedTab(LeftRailTab.GOTO);
  }

  /**
   * Open a flyout content tab (alignments, notes, score, etc.). Left-side
   * flyouts (parent activity alignments) occupy the rail. Right-side flyouts
   * (standalone items and activity children) stay independent. Clicking the
   * selected left-rail tab closes the rail.
   */
  toggleFlyoutContentTab = (innerTab, openState, lessonElementId) => {
    const os = navMenuManager.getFlyoutOpenState(lessonElementId);
    if (!os) {
      return;
    }
    const flyoutIsOpen = os.open === FlyoutOpenState.OPEN ||
      os.open === FlyoutOpenState.OPEN_WIDE ||
      os.open === FlyoutOpenState.OPEN_WIDER;
    const isThisTab = os.tab === innerTab;
    const hostedInLeftRail = os.side === FlyoutSide.LEFT && this.hasLeftRail();

    if (hostedInLeftRail) {
      if (this.isOpen && this.selectedTab === LeftRailTab.FLYOUT && flyoutIsOpen && isThisTab) {
        this.close();
        return;
      }
      this.open({ selectedTab: LeftRailTab.FLYOUT });
      navMenuManager.setFlyoutOpenState({
        open: openState,
        tab: innerTab,
        side: FlyoutSide.LEFT
      }, lessonElementId);
      return;
    }

    if (flyoutIsOpen && isThisTab) {
      navMenuManager.setFlyoutOpenState({
        open: FlyoutOpenState.CLOSED,
        tab: innerTab,
        side: os.side
      }, lessonElementId);
      return;
    }
    navMenuManager.setFlyoutOpenState({
      open: openState,
      tab: innerTab,
      side: os.side
    }, lessonElementId);
  }

  /**
   * Apply satellite open state from the parent iframe without echoing a
   * postMessage back. Only used when the parent explicitly opened or closed
   * related content, not when a tab merely becomes visible.
   */
  syncSatelliteOpenFromParent = (isOpen) => {
    if (isOpen) {
      this.setOpen(true, { echoSatellite: false, selectedTab: LeftRailTab.SATELLITE });
      return;
    }
    this.setOpen(false, { echoSatellite: false });
  }

  /**
   * Related-content tab visibility changed for this page. The rail stays
   * open. The tab joins the list and does not become selected if GO TO (or
   * another tab) is present.
   */
  syncRelatedTabVisibility = (show) => {
    lessonManager.setShouldShowRelatedTabButton(show);
    if (!this.isOpen) {
      return;
    }
    if (show) {
      if (this.hasGoto()) {
        if (this.selectedTab === LeftRailTab.SATELLITE || !this.selectedTab) {
          this.setSelectedTab(LeftRailTab.GOTO);
        } else {
          this.postSatelliteMessage('close_satellite');
        }
        return;
      }
      this.setSelectedTab(LeftRailTab.SATELLITE);
      return;
    }
    this.postSatelliteMessage('close_satellite');
    if (this.selectedTab === LeftRailTab.SATELLITE && this.hasGoto()) {
      this.setSelectedTab(LeftRailTab.GOTO, { echoSatellite: false });
    }
  }

  hasTopMostAlternateLeftTab = () => {
    return this.hasGoto();
  }

  /**
   * Related content is gone for the current slide. Hide that tab, keep the
   * rail open, and keep GO TO selected when it exists.
   */
  onSatelliteContentUnavailable = () => {
    lessonManager.setShouldShowRelatedTabButton(false);
    if (this.hasGoto()) {
      this.open({ echoSatellite: false, selectedTab: LeftRailTab.GOTO });
      this.postSatelliteMessage('close_satellite');
      return;
    }
    lessonManager.setShouldShowRelatedTabButton(true);
    lessonManager.setIsSatelliteOpen(true);
    navMenuManager.setLeftRailSelectedTab(LeftRailTab.SATELLITE);
    this.postSatelliteMessage('satellite_empty');
  }

  handleParentSatelliteMessage = (event) => {
    if (!event.data || !event.data.type) {
      return;
    }
    if (event.data.type === 'related_tab_button_visibility') {
      if (typeof event.data.show === 'boolean') {
        this.syncRelatedTabVisibility(event.data.show);
      }
      // Parent explicitly opened related content while the rail was closed.
      // Ignore open:true while the rail is already open so an unhidden tab
      // cannot steal selection from GO TO. Ignore open:false so navigation
      // cannot collapse the rail.
      if (event.data.open === true && event.data.show !== false && !this.isOpen) {
        this.syncSatelliteOpenFromParent(true);
      }
      return;
    }
    if (event.data.type === 'satellite_content_unavailable') {
      this.onSatelliteContentUnavailable();
    }
  }
}

export default new LeftRailService();
